import { OrdersDtoOrderLineDto } from "@springtree/eva-services-core";
import classNames from "classnames";
import { motion } from "framer-motion";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { RefObject, useEffect, useMemo, useState } from "react";

import IconDoubleChevronUp from "@/icons/icon-double-chevron-up";
import { sequentialLoadingState } from "~/atoms/sequential-loading";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useConfiguration } from "~/contexts/configuration";
import { useCart, useCartAvailability } from "~/hooks/use-cart";
import appRoutes from "~/utils/app-routes";
import cartUtils from "~/utils/cart-utils";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";
import intlUtils from "~/utils/intl-utils";
import { useTranslateWithFallback } from "~/utils/translation-utils";

import Button from "../common/button";
import Icon from "../common/icon";
import DiscountLinesTotals from "../products/discount-lines-totals";
import CartOOSDialog from "./cart-oos-dialog";
import styles from "./cart-totals.module.scss";

type Props = {
  refWrapperCartTotalsOnMobile?: RefObject<HTMLDivElement>;
  isSticked?: boolean;
};

export default function CartTotals(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const priceFormatter = intlUtils.usePriceFormatter();
  const { configuration } = useConfiguration();
  const cart = useCart();
  const cartAvailability = useCartAvailability();
  const { isAuthenticated } = useAuthContext();
  const [oosDialogOpen, setOosDialogOpen] = useState(false);
  const [openTotals, setOpenTotals] = useState(false);
  const breakpoint = useBreakpoint();
  const translateWithFallback = useTranslateWithFallback();
  const sequentialLoadingReady = useAtomValue(sequentialLoadingState);

  const totals = useMemo(
    () => cartUtils.calculateCartTotals(cart, configuration.PreferredPriceDisplayMode == 1),
    [cart, configuration.PreferredPriceDisplayMode]
  );
  const currency = cart?.ShoppingCart?.CurrencyID ?? configuration?.CurrencyID;

  const url = isAuthenticated ? appRoutes.CHECKOUT : { pathname: appRoutes.LOGIN, query: { origin: "cart" } };

  const notOnlyDigital =
    cart &&
    cart.ShoppingCart?.Lines &&
    cart.ShoppingCart?.Lines?.length > 0 &&
    cart.ShoppingCart.Lines.some((el) => !el.IsCancelled && !el.IsDigitalGiftCard);

  const totalDigital =
    (cart &&
      cart.ShoppingCart?.Lines &&
      cart.ShoppingCart.Lines.length > 0 &&
      cart.ShoppingCart.Lines.filter((el) => !el.IsCancelled && el.IsDigitalGiftCard)
        ?.map((el) => el.UnitPriceInTax)
        ?.reduce((accumulator, currentValue) => accumulator + currentValue, 0)) ||
    0;

  const reachedFreeShipping =
    cart && cart.ShoppingCart?.Lines && cart.ShoppingCart.Lines.length > 0 && totals.shippingAmount === 0;
  const lowestFreeShippingMethod = cartUtils.findShippingMethodWithLowestFreeShipping(cart);
  const requiredForFreeShipping = lowestFreeShippingMethod?.amount ?? 0;
  const remainingForFreeShipping = requiredForFreeShipping - (totals.total - totals.shippingAmount - totalDigital);
  const freeShippingBarProgress = Math.max(Math.min(1 - remainingForFreeShipping / requiredForFreeShipping, 1), 0);

  const technicallyReachedFreeShipping = reachedFreeShipping || remainingForFreeShipping <= 0;

  const outOfStockProductsForDelivery = useMemo(
    () =>
      cartAvailability?.Products?.filter((p) => !p.Delivery?.HasStock)
        .map((p) => p.ProductID)
        .map((pid) => cart?.ShoppingCart?.Lines?.find((line) => line.ProductID === pid))
        .filter((item) => item !== undefined) ?? [],
    [cartAvailability, cart]
  );

  useEffect(() => {
    const usableNetContainer = document.getElementById(Constants.USABLENET_CONTAINER_KEY);
    setTimeout(() => {
      if (sequentialLoadingReady.usablenet && usableNetContainer && breakpoint === "mobile") {
        if (!props.isSticked) {
          usableNetContainer.style.bottom = "";
        } else if (props.refWrapperCartTotalsOnMobile?.current) {
          usableNetContainer.style.bottom = props.refWrapperCartTotalsOnMobile.current.offsetHeight + 20 + "px";
        }
      }
    }, 300);

    return () => {
      if (usableNetContainer) {
        if (props.refWrapperCartTotalsOnMobile?.current && props.isSticked) {
          usableNetContainer.style.bottom = props.refWrapperCartTotalsOnMobile.current.offsetHeight + 20 + "px";
        } else {
          usableNetContainer.style.bottom = "";
        }
      }
    };
  }, [openTotals, breakpoint, props.isSticked, props.refWrapperCartTotalsOnMobile, sequentialLoadingReady.usablenet]);

  return (
    <div className={styles.container}>
      {breakpoint === "mobile" ? (
        <button className={styles.wrapperIcon} onClick={() => setOpenTotals(!openTotals)}>
          <Icon
            icon={IconDoubleChevronUp}
            width={16}
            height={16}
            className={openTotals ? styles.IconDoublechevronDown : styles.IconDoublechevronUp}
          />
        </button>
      ) : null}
      <div
        className={classNames(
          styles.wrapperPricesAndFreeShipping,
          openTotals || breakpoint === "desktop" ? styles.openPricesAndFreeShipping : undefined
        )}
      >
        <div className={styles.innerWrapperPricesAndFreeShipping}>
          <div className={styles.prices}>
            <div className={styles.row}>
              <p className={styles.rowTitle}>{t("generic.subtotal")}</p>
              <p className={styles.rowValue}>{priceFormatter.currency(totals.subtotal, { currency })}</p>
            </div>
            {totals.discountLines && totals.discountLines.length > 0 && (
              <DiscountLinesTotals lines={totals.discountLines} currency={currency} />
            )}
            {totals.giftWrapping != 0 && (
              <div className={styles.row}>
                <p className={styles.rowTitle}>{t("generic.giftWrappingCost")}</p>
                <p className={styles.rowValue}>{priceFormatter.currency(totals.giftWrapping, { currency })}</p>
              </div>
            )}
            {cart?.ShoppingCart?.HasDelivery && (
              <div className={styles.row}>
                <p className={styles.rowTitle}>
                  {t("generic.shipping_to", {
                    destination: crossCountryUtils.getCurrentCountryCode(router),
                    method: totals.shippingMethod
                      ? translateWithFallback(
                          `generic.shipping.shippingMethodName.${totals.shippingMethodCode}`,
                          totals.shippingMethod
                        )
                      : lowestFreeShippingMethod?.method.Name,
                  })}
                </p>
                <p className={styles.rowValue}>
                  {totals.shippingAmount > 0 && totals.estimated
                    ? t.rich("generic.cart.estimated_total", {
                        total: priceFormatter.currency(totals.shippingAmount, { currency }),
                        regular: (chunks) => <span className={styles.estimated}>{chunks}</span>,
                      })
                    : totals.shippingAmount > 0
                    ? priceFormatter.currency(totals.shippingAmount, { currency })
                    : t("generic.free")}
                </p>
              </div>
            )}
            {totals.tax != null && totals.tax !== 0 && (
              <div className={styles.row}>
                <p className={styles.rowTitle}>{t("generic.taxes")}</p>
                <p className={styles.rowValue}>{priceFormatter.currency(totals.tax, { currency })}</p>
              </div>
            )}
          </div>
          {requiredForFreeShipping > 0 && notOnlyDigital && (
            <div className={styles.freeShippingContainer}>
              <p className={styles.freeShippingText}>
                {technicallyReachedFreeShipping ? (
                  <>{t("generic.cart.freeShippingReached", { method: lowestFreeShippingMethod?.method.Name })}</>
                ) : (
                  <>
                    {t.rich("generic.cart.freeShippingRemaining", {
                      span: (chunks) => <span className={styles.freeShippingMissing}>{chunks}</span>,
                      method: lowestFreeShippingMethod?.method.Name,
                      amount: priceFormatter.currency(remainingForFreeShipping, {
                        currency,
                      }),
                    })}
                  </>
                )}
              </p>
              <div className={styles.freeShippingMeter}>
                <div className={styles.freeShippingBar}>
                  <motion.div
                    className={styles.freeShippingFill}
                    initial={{ width: 0 }}
                    animate={{ width: technicallyReachedFreeShipping ? "100%" : freeShippingBarProgress * 100 + "%" }}
                    transition={{ delay: 0.2 }}
                  />
                </div>
                {/* <p className={styles.freeShippingTotal}>
              {priceFormatter.currency(25, { currency })}
            </p> */}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.totalContainer}>
        <p>
          {t("generic.total")}{" "}
          {configuration.PreferredPriceDisplayMode == 1 && totals.tax == 0 && (
            <span className={styles.vatText}>{t("generic.vatExcluded")}</span>
          )}
        </p>
        <p>{priceFormatter.currency(totals.total, { currency })}</p>
        {configuration.PreferredPriceDisplayMode == 1 && totals.tax == 0 && (
          <p className={styles.vatNotCalculatedYet}>{t("generic.insertBillingToCalculateTaxes")}</p>
        )}
      </div>
      <div className={styles.checkoutLink}>
        {!!cart?.ShoppingCart?.TotalItems && cart.ShoppingCart.TotalItems > 0 ? (
          <Button
            variant="primary"
            onClick={() => {
              if (outOfStockProductsForDelivery.length) {
                setOosDialogOpen(true);
              } else {
                return router.push(url as string);
              }
            }}
            data-dd-action-name={isAuthenticated ? "DD_CHECKOUT_AUTHENTICATED" : "DD_CHECKOUT_NOT_AUTHENTICATED"}
          >
            {t("generic.checkout.title")}
          </Button>
        ) : (
          <Button variant="primary" disabled className={styles.checkoutLink} data-dd-action-name="DD_CHECKOUT_DISABLED">
            {t("generic.checkout.title")}
          </Button>
        )}
      </div>
      {outOfStockProductsForDelivery?.length > 0 ? (
        <CartOOSDialog
          isOpen={oosDialogOpen}
          onClose={() => setOosDialogOpen(false)}
          outOfStockDeliveryOrderLines={
            outOfStockProductsForDelivery.filter((item) => item !== undefined) as OrdersDtoOrderLineDto[]
          }
        />
      ) : null}
    </div>
  );
}
